import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faDownload } from '@fortawesome/free-solid-svg-icons';
import T from 'i18n';

interface IManageDoc {
  documentId: string;
  name: string;
  extension?: string;
}

interface IManageJobSummaryDialogProps {
  open: boolean;
  successfulDocs: IManageDoc[];
  failedDocs: IManageDoc[];
  onProceed: () => void;
  onSelectAgain: () => void;
}

function buildDisplayName(doc: IManageDoc): string {
  return doc.extension ? `${doc.name}.${doc.extension}` : doc.name;
}

function truncate(name: string): string {
  return name.length > 40 ? `${name.substring(0, 18)}...${name.substring(name.length - 18)}` : name;
}

function escapeCsv(value: string): string {
  const escapedValue = value.replace(/"/g, '""');
  return /[",\n\r]/.test(value) ? `"${escapedValue}"` : escapedValue;
}

function exportCSV(successfulDocs: IManageDoc[], failedDocs: IManageDoc[]) {
  const rows = [
    ['documentId', 'fileName', 'extension', 'status'],
    ...successfulDocs.map((d) => [d.documentId, d.name, d.extension ?? '', 'Success']),
    ...failedDocs.map((d) => [d.documentId, d.name, d.extension ?? '', 'Failed']),
  ];
  const csv = rows.map((r) => r.map((v) => escapeCsv(v)).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'imanage-import-summary.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const DocList: React.FC<{ docs: IManageDoc[]; success: boolean }> = ({ docs, success }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
    {docs.map((doc) => {
      const displayName = buildDisplayName(doc);
      return (
        <li
          key={doc.documentId}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}
        >
          <FontAwesomeIcon
            icon={success ? faCheckCircle : faTimesCircle}
            style={{ color: success ? 'green' : '#d32f2f', flexShrink: 0 }}
          />
          <span title={displayName} style={{ fontSize: 13 }}>
            {truncate(displayName)}
          </span>
        </li>
      );
    })}
  </ul>
);

const IManageJobSummaryDialog: React.FC<IManageJobSummaryDialogProps> = ({
  open,
  successfulDocs,
  failedDocs,
  onProceed,
  onSelectAgain,
}) => {
  const allFailed = successfulDocs.length === 0;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle style={{ paddingBottom: 8 }}>
        {T.translate('case.batchUpload.iManage.summary.title')}
      </DialogTitle>

      <DialogContent dividers style={{ paddingTop: 12 }}>
        {successfulDocs.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: 6, color: 'green' }}
            >
              {T.translate('case.batchUpload.iManage.summary.successHeader', {
                count: successfulDocs.length,
              })}
            </Typography>
            <DocList docs={successfulDocs} success={true} />
          </>
        )}

        {successfulDocs.length > 0 && failedDocs.length > 0 && (
          <Divider style={{ margin: '12px 0' }} />
        )}

        {failedDocs.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: 6, color: '#d32f2f' }}
            >
              {T.translate('case.batchUpload.iManage.summary.failedHeader', {
                count: failedDocs.length,
              })}
            </Typography>
            <DocList docs={failedDocs} success={false} />
          </>
        )}

        {allFailed && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 12, fontStyle: 'italic' }}
          >
            {T.translate('case.batchUpload.iManage.summary.allFailedMessage')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: 'space-between', padding: '12px 16px' }}>
        <Button
          size="small"
          startIcon={<FontAwesomeIcon icon={faDownload} />}
          onClick={() => exportCSV(successfulDocs, failedDocs)}
          variant="outlined"
        >
          {T.translate('case.batchUpload.iManage.summary.exportList')}
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" onClick={onSelectAgain} variant="outlined">
            {T.translate('case.batchUpload.iManage.summary.selectAgain')}
          </Button>
          <Button
            size="small"
            onClick={onProceed}
            variant="contained"
            color="primary"
            disabled={allFailed}
          >
            {T.translate('case.batchUpload.iManage.summary.proceed')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default IManageJobSummaryDialog;
